import React, { useState } from 'react';
import { Modal, Spin, Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { ITravelAttachment } from '../../../types';
import {
  useLazyGetAttachmentGalleryFileQuery,
  useLazyGetTravelAttachmentQuery,
} from '../../../store';
import { DownloadIcon, EyeIcon, TrashIcon } from 'lucide-react';
import { GlobalLoader } from '../..';
import { formatDayJsDate } from '../../../utils/dateTimeUtils';
import dayjs from 'dayjs';
import { useAuth, useNotification } from '../../../contexts';
import {
  previewAttachment,
  downloadAttachment,
} from '../../../utils/fileUtils';
interface Props {
  attachments: ITravelAttachment[];
  travelRequestId: number;
  viewOnly?: boolean;
  onDelete?: (attachmentId: string | number) => void;
}

const AttachmentTable: React.FC<Props> = ({
  attachments,
  travelRequestId,
  viewOnly = false,
  onDelete,
}) => {
  const visibleAttachments = attachments.filter(att => !att.deleteFlag);
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string | null>(null);

  const [getAttachment, { isFetching }] = useLazyGetTravelAttachmentQuery();
  const [getGalleryAttachment, { isFetching: isGalleryFetching }] =
    useLazyGetAttachmentGalleryFileQuery();

  const handlePreview = async (attachment: ITravelAttachment) => {
    const succeeded = await previewAttachment(
      attachment,
      getAttachment,
      setPreviewFile,
      setPreviewName,
      travelRequestId,
      getGalleryAttachment,
    );
    if (!succeeded) {
      showNotification(
        'error',
        'Preview unavailable',
        'The attachment could not be opened. Please try again.',
      );
    }
  };

  const handleDownload = async (attachment: ITravelAttachment) => {
    const succeeded = await downloadAttachment(
      attachment,
      getAttachment,
      travelRequestId,
      getGalleryAttachment,
    );
    if (!succeeded) {
      showNotification(
        'error',
        'Download failed',
        'The attachment could not be downloaded. Please try again.',
      );
    }
  };

  const columns: ColumnType<ITravelAttachment>[] = [
    {
      title: 'Document Type',
      dataIndex: 'documentType',
      key: 'documentType',
    },
    {
      title: 'File',
      dataIndex: 'fileName',
      key: 'fileName',
      render: (_, record) => {
        if (isNaN(+record.id)) {
          return record.file ? (
            <Tooltip title={record.file.name}>
              <div className="text-accent-500 cursor-pointer w-[200px] line-clamp-2 text-ellipsis text-wrap">
                {record?.file?.name || '-'}
              </div>
            </Tooltip>
          ) : (
            <div className="text-red-500">No file attached</div>
          );
        } else {
          return (
            <Tooltip title={record.fileName}>
              <div className="text-accent-500 cursor-pointer w-[200px] line-clamp-2 text-ellipsis text-wrap">
                {record?.fileName || '-'}
              </div>
            </Tooltip>
          );
        }
      },
    },
    {
      title: 'Created By',
      dataIndex: 'createdByUser',
      key: 'createdByUser',
      render: (_, record) => {
        const fullName = record?.createdByUser?.FullName;
        const employeeCode = record?.createdByUser?.EmployeeCode;

        if (fullName && employeeCode) {
          return `${fullName} - ${employeeCode}`;
        }
        if (fullName) {
          return fullName;
        }
        return '-';
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record) => (
        <span>
          {record?.createdAt
            ? formatDayJsDate(dayjs(record?.createdAt), 'verbose')
            : '-'}
        </span>
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      render: (_, record) => {
        return (
          <div className="w-[300px] line-clamp-2 text-ellipsis text-wrap">
            <Tooltip title={record?.remarks || '-'}>
              {record?.remarks || '-'}
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record: ITravelAttachment) => {
        const newLineItem = isNaN(+record.id);
        return (
          <div className="flex gap-3 items-center text-gray-500">
            {user?.ID == record?.createdById || newLineItem ? (
              <>
                {!viewOnly && onDelete && (
                  <Tooltip title="Delete" placement="top">
                    <TrashIcon
                      className="size-4 cursor-pointer transition-colors hover:text-red-600 stroke-[3px]"
                      onClick={() => onDelete(record.id)}
                    />
                  </Tooltip>
                )}

                <Tooltip title="View" placement="top">
                  <EyeIcon
                    className="size-4 cursor-pointer transition-colors hover:text-green-600 stroke-[3px]"
                    onClick={() => handlePreview(record)}
                  />
                </Tooltip>

                <Tooltip title="Download" placement="top">
                  <DownloadIcon
                    className="size-4 cursor-pointer transition-colors hover:text-blue-600 stroke-[3px]"
                    onClick={() => handleDownload(record)}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="View" placement="top">
                  <EyeIcon
                    className="size-4 cursor-pointer transition-colors hover:text-green-600 stroke-[3px]"
                    onClick={() => handlePreview(record)}
                  />
                </Tooltip>

                <Tooltip title="Download" placement="top">
                  <DownloadIcon
                    className="size-4 cursor-pointer transition-colors hover:text-blue-600 stroke-[3px]"
                    onClick={() => handleDownload(record)}
                  />
                </Tooltip>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-slate-200/80 bg-white shadow-2xs">
        <table className="w-full border-spacing-y-0 text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur-xs border-b border-slate-200/80">
            <tr>
              {columns.map(column => (
                <th
                  key={column.key as string}
                  className={`px-4 py-3 text-xs font-semibold tracking-tight text-left whitespace-nowrap text-slate-600 ${
                    column.fixed === 'right'
                      ? 'sticky right-0 bg-slate-50/95 z-20'
                      : ''
                  }`}
                >
                  {column.title as React.ReactNode}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {visibleAttachments.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-6 text-center text-xs font-medium"
                >
                  <div className="text-xs text-text-300 font-semibold">
                    No attachments yet
                  </div>
                  <div className="mt-1 text-[11px] font-normal text-text-500">
                    Supporting documents will appear here after they are added.
                  </div>
                </td>
              </tr>
            ) : (
              visibleAttachments.map((record, rowIndex) => (
                <tr
                  key={record.id}
                  className={`${
                    rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  } border-t border-gray-100 hover:bg-gray-50 transition-colors duration-200`}
                >
                  {columns.map(column => (
                    <td
                      key={column.key as string}
                      className={`px-4 py-3.5 text-left text-xs text-text-200 whitespace-nowrap ${
                        column.fixed === 'right'
                          ? 'sticky right-0 bg-inherit z-10'
                          : ''
                      }`}
                    >
                      {typeof column.render === 'function'
                        ? (column.render(
                            record[
                              column.dataIndex as keyof ITravelAttachment
                            ] as any,
                            record,
                            rowIndex,
                          ) as any)
                        : (record?.[
                            column.dataIndex as keyof ITravelAttachment
                          ] as any)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal for Preview */}
      <Modal
        title={previewName}
        open={!!previewFile}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        width={800}
      >
        {isFetching || isGalleryFetching ? (
          <Spin size="large" />
        ) : (
          previewFile && (
            <iframe src={previewFile} width="100%" height="500px"></iframe>
          )
        )}
      </Modal>

      <GlobalLoader
        loading={isFetching || isGalleryFetching}
        message="Preparing file..."
      />
    </div>
  );
};

export default AttachmentTable;
